import { showErrorToast } from '@bpinternal/ui-kit-next'
import { useQueryClient } from '@tanstack/react-query'
import { useAsync } from 'react-use'

import { DEFAULT_WORKSPACE_NAME } from '../features/workspaces/types'
import { useCreateWorkspace, useCurrentUser, useWorkspaces } from '../hooks'
import { userPreferencesQueryOptions } from '../queries'
import { getQueryKey } from '../services'
import { setAccountPreference } from '../services/preferences/userPreferences'

type Props = {
  children?: React.ReactNode
}

export const WorkspaceProvider = ({ children }: Props) => {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const { data: workspaces } = useWorkspaces()
  const { mutateAsync: createWorkspace } = useCreateWorkspace()

  async function ensureDefaultWorkspace() {
    if (workspaces && workspaces.length === 0) {
      await createWorkspace({ name: DEFAULT_WORKSPACE_NAME })
      // Set requireOnboarding before invalidating workspaces so the loader reads the correct value.
      // Non-blocking: if it fails, the user just skips the survey.
      await setAccountPreference({ path: 'requireOnboarding', value: true }).catch(() => {})
      queryClient.setQueryData(userPreferencesQueryOptions({ path: 'requireOnboarding' }).queryKey, true)
      await queryClient.invalidateQueries({ queryKey: getQueryKey('workspaces_') })
    }
  }

  useAsync(async () => {
    if (workspaces) {
      await ensureDefaultWorkspace().catch((e) => {
        showErrorToast(
          "Sorry, we couldn't automatically create a workspace for you. Please try creating your first workspace manually."
        )
        // eslint-disable-next-line no-console
        console.error(e)
      })
    }
  }, [user, workspaces])

  if (!workspaces?.length) {
    return null
  }

  return <>{children}</>
}
